/**
 * Brand presentation helpers for the public mailer landing page.
 *
 * The colour is whatever the contractor saved in their own settings, so it can
 * be any hue — including a pale one where white text vanishes. Anything drawn
 * on top of the brand colour picks its foreground from that colour's luminance
 * rather than assuming white.
 */

const HEX_COLOR = /^#(?:[0-9a-f]{3}|[0-9a-f]{6})$/i;

/** Used when the organization never picked a colour. */
const FALLBACK_BRAND = '#1F4FD8';

export interface BrandPalette {
  base: string;
  /** Foreground that stays legible on `base`. */
  onBase: string;
  /** Faint wash of the brand colour for section backgrounds. */
  tint: string;
  border: string;
}

function toRgb(hex: string): [number, number, number] {
  const digits =
    hex.length === 4
      ? hex
          .slice(1)
          .split('')
          .map((char) => char + char)
          .join('')
      : hex.slice(1);
  return [
    parseInt(digits.slice(0, 2), 16),
    parseInt(digits.slice(2, 4), 16),
    parseInt(digits.slice(4, 6), 16),
  ];
}

/** WCAG relative luminance, used only to choose a dark or light foreground. */
function luminance([red, green, blue]: [number, number, number]): number {
  const channel = (value: number): number => {
    const scaled = value / 255;
    return scaled <= 0.03928 ? scaled / 12.92 : ((scaled + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * channel(red) + 0.7152 * channel(green) + 0.0722 * channel(blue);
}

export function brandPalette(color: string | null | undefined): BrandPalette {
  // The value lands in a style declaration, so anything that is not a plain
  // hex colour is discarded rather than passed through.
  const trimmed = (color ?? '').trim();
  const base = HEX_COLOR.test(trimmed) ? trimmed : FALLBACK_BRAND;
  const [red, green, blue] = toRgb(base);
  return {
    base,
    onBase: luminance([red, green, blue]) > 0.55 ? '#10161C' : '#FFFFFF',
    tint: `rgba(${red}, ${green}, ${blue}, 0.08)`,
    border: `rgba(${red}, ${green}, ${blue}, 0.3)`,
  };
}

/** `(555) 123-4567` for a bare NANP number; anything else is shown as entered. */
export function formatPhoneNumber(phone: string): string {
  const digits = phone.replace(/\D/g, '');
  if (digits.length === 11 && digits.startsWith('1')) {
    return formatPhoneNumber(digits.slice(1));
  }
  if (digits.length !== 10) return phone;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
}

export function telHref(phone: string): string {
  return `tel:${phone.replace(/[^\d+]/g, '')}`;
}
